import type { Config } from "contexts/CollectionDetails";
import { AppRoutes, isCaptureType } from "types";
import { getWorkTaskSections } from "./rtms-sequence";
import type { ResolvedWorkTask, ResolvedWorkTaskStep } from "./work-tasks";

const PREFIX = "dc-open:";
const ENTRY_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export interface TaskNavigationTarget {
  v: 1;
  collection_id: string;
  task_id: string;
  entry_id?: string;
}

export function readTaskNavigationQr(
  raw: string,
  collectionId: string
): TaskNavigationTarget {
  if (raw.length > 2048 || !raw.startsWith(PREFIX))
    throw new Error("Scan a station or task QR code for this campaign.");
  let value: unknown;
  try {
    value = JSON.parse(raw.slice(PREFIX.length));
  } catch {
    throw new Error("This station QR code is invalid.");
  }
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("This station QR code is invalid.");
  const target = value as Record<string, unknown>;
  if (
    target.v !== 1 ||
    Object.keys(target).some(
      (key) => !["v", "collection_id", "task_id", "entry_id"].includes(key)
    ) ||
    ![target.collection_id, target.task_id].every(
      (id) =>
        typeof id === "string" &&
        id.length > 0 &&
        id.length <= 128 &&
        id.trim() === id &&
        !/\s/.test(id) &&
        Array.from(id).every(
          (char) => char.charCodeAt(0) > 31 && char.charCodeAt(0) !== 127
        )
    ) ||
    (target.entry_id !== undefined &&
      (typeof target.entry_id !== "string" || !ENTRY_ID.test(target.entry_id)))
  )
    throw new Error("This station QR code is invalid or unsupported.");
  if (!collectionId || target.collection_id !== collectionId)
    throw new Error("This QR code belongs to a different campaign.");
  return target as unknown as TaskNavigationTarget;
}

export function createTaskNavigationQr(target: TaskNavigationTarget): string {
  const content = PREFIX + JSON.stringify(target);
  readTaskNavigationQr(content, target.collection_id);
  return content;
}

export function getTaskNavigationTask(
  target: TaskNavigationTarget,
  config: Config,
  collectionId: string
) {
  readTaskNavigationQr(createTaskNavigationQr(target), collectionId);
  if (config.selectedCollection?.data.enable_task_qr_navigation !== true)
    throw new Error("Station scanning is not enabled for this campaign.");
  const matches = Object.values(config.tasks).filter(
    (task) => task.task_id === target.task_id
  );
  const definitions = config.selectedCollection.data.tasks?.filter(
    (task) => task.task_id?.trim() === target.task_id
  );
  if (matches.length !== 1 || (definitions && definitions.length > 1))
    throw new Error("This station or task cannot be identified uniquely.");
  const task = matches[0];
  if (
    definitions?.[0]?.work_task?.link_type === "Document" &&
    !task.workTaskDocumentId
  )
    throw new Error(
      "This Work Task link is unavailable. Please contact the collection manager."
    );
  if (!task.enabled)
    throw new Error("This station or task is not available in this campaign.");
  if (target.entry_id && !task.workTaskDocumentId)
    throw new Error("This QR does not identify a task inside a Work Task.");
  if (!task.workTaskDocumentId && !isCaptureType(task.capture_type))
    throw new Error("This task's capture type is not available.");
  return task;
}

export function getTaskNavigationRoute(
  task: ReturnType<typeof getTaskNavigationTask>
): AppRoutes {
  if (task.workTaskDocumentId) return AppRoutes.taskInstructions;
  switch (task.capture_type) {
    case "orb":
      return AppRoutes.orb;
    case "rtms":
      return AppRoutes.stepRtms;
    case "iris":
      return AppRoutes.irisUpload;
    case "pcr":
      return AppRoutes.pcrCapture;
    case "file":
      return AppRoutes.batchUpload;
    case "video":
      return AppRoutes.videoUpload;
    default:
      return AppRoutes.capture;
  }
}

export function getTaskNavigationSteps(
  flow: ResolvedWorkTask,
  entryId?: string
): ResolvedWorkTaskStep[] {
  if (!entryId) return flow.steps;
  const matches = flow.steps.filter((step) => step.captureEntryId === entryId);
  if (
    matches.length === 0 ||
    new Set(matches.map((step) => step.captureTaskIndex)).size !== 1
  )
    throw new Error(
      "This task position is missing or has a duplicate entry ID."
    );
  // A collector section is atomic: never launch in the middle of its QR plan.
  return matches[0].rtmsSequence
    ? getWorkTaskSections(flow)
        .filter((section) =>
          section.some(
            (step) => step.captureTaskIndex === matches[0].captureTaskIndex
          )
        )
        .flat()
    : matches;
}
